import React from 'react';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import {FaStar} from 'react-icons/fa';
import {CiCamera} from 'react-icons/ci';
import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {postPhotoFetcher, postFetcher} from "../../../api/fetchers.ts";
import {endpoints} from "../../../api/endpoints.ts";
import { useLanguage } from '../../context/LanguageContext.tsx';

interface SellerRateFormProps {
    groupOrders: any[];
}

interface SellerRateFormValues {
    supplierId: string;
    rating: number;
    review: string;
    images: {
        image1: string;
        image2: string;
        image3: string;
    };
}

const SellerRateForm: React.FC<SellerRateFormProps> = ({groupOrders}) => {
    const {t} = useLanguage();
    // const [isPrivacyModalOpen, setPrivacyModalOpen] = useState(false);
    // const [isTermsModalOpen, setTermsModalOpen] = useState(false);


    const sellerValidationSchema = Yup.object().shape({
        rating: Yup.number().min(1, 'Rating is required').required('Rating is required'),
        review: Yup.string().required('Comment is required')
    });

    const initialValues: SellerRateFormValues = {
        supplierId: groupOrders.length > 0 ? groupOrders[0].supplierInfo.supplierId : '',
        rating: 0,
        review: '',
        images: {
            image1: '',
            image2: '',
            image3: ''
        }
    };



    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, imageIndex: number, setFieldValue: (field: string, value: any) => void) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const fileExtension = file.name.split('.').pop()?.toLowerCase();
            if (fileExtension === 'svg') {
                toast.error(t('toastMessage', 'rate9'));
                return;
            }
            const formData = new FormData();
            formData.append('file', file);

            try {
                const response = await postPhotoFetcher(endpoints.updateImage.updateImageSingle, formData);
                if (response && response.data && response.data.url) {
                    setFieldValue(`images.image${imageIndex + 1}`, response.data.url);
                    toast.success(t('toastMessage', 'rate10'));
                } else {
                    toast.error(t('toastMessage', 'rate11'));
                }
            } catch (error) {
                toast.error(t('toastMessage', 'rate11'));
                console.error('Error uploading photo:', error);
            }
        }
    };
    
    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={sellerValidationSchema}
                onSubmit={async (values, {resetForm}) => {
                    const formData = {
                        supplierId: values.supplierId,
                        rating: values.rating,
                        review: values.review,
                        images: values.images
                    };

                    try {
                        const response = await postFetcher(endpoints.products.postSupplierEvaluation, {arg: formData});
                        if (response.result) {
                            toast.success(t('toastMessage', 'rate13'));
                            resetForm();
                        } else {
                            let errorMessage = response?.message || t('toastMessage', 'rate14');
                            if (errorMessage === "Supplier evaluation already exists.") {
                                 errorMessage = t('toastMessage', 'rate14')
                            } else if (errorMessage === "Please provide all required fields.") {
                                toast.error(response?.message || 'Tüm alanlar zorunludur.');
                            }
                            toast.error(`${errorMessage}`);
                        }
                    } catch (error) {
                        toast.error(t('toastMessage', 'rate14'));
                        console.error('Error submitting evaluation:', error);
                    }
                }}
            >
                {({values, setFieldValue}) => (
                    <Form className="md:p-12">
                        {groupOrders.length > 0 && (
                            groupOrders.map((order, idx) => (
                                <div key={idx} className="mb-6 mt-6 flex items-center flex-wrap ">
                                    <img src={(order.productInfo.images)[0]}
                                         alt={order.supplierInfo.supplierFullName} className="w-12 h-12 mr-4 mb-2"/>
                                    <h3 className="font-bold text-xl">{order.supplierInfo.supplierMarketPlaceName}</h3>
                                </div>
                            ))
                        )}
                        <div className="mb-6">
                            <h4 className="font-semibold mb-2 text-sm sm:text-base">SATICIYI DEĞERLENDİRİNİZ</h4>
                            <div className="flex mb-2">
                                {[...Array(5)].map((_, index) => (
                                    <FaStar
                                        key={index}
                                        className={`cursor-pointer text-2xl sm:text-3xl ${index < values.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                                        onClick={() => setFieldValue('rating', index + 1)}
                                    />
                                ))}
                            </div>
                            <ErrorMessage name="rating" component="div" className="text-red-500 text-xs"/>
                        </div>

                        <div className="mb-6">
                            <h4 className="font-semibold mb-2 text-sm sm:text-base">Yorumunuz</h4>
                            <Field
                                as="textarea"
                                className="w-full p-2 border rounded text-sm sm:text-base"
                                rows={4}
                                name="review"
                                placeholder="Satıcı ile ilgili yorumunuzu belirtiniz."
                            />
                            <ErrorMessage name="review" component="div" className="text-red-500 text-xs"/>
                        </div>

                        <div className="mb-6">
                            <h4 className="font-semibold mb-2 text-sm sm:text-base">Fotoğraf Ekle</h4>
                            <div className="flex flex-wrap gap-12">
                                {[0, 1, 2].map((index) => (
                                    <label
                                        key={index}
                                        className="w-20 h-20 sm:w-28 sm:h-28 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer">
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={(event) => handleImageUpload(event, index, setFieldValue)}
                                        />
                                        {values.images[`image${index + 1}` as keyof typeof values.images] ? (
                                            <img
                                                src={values.images[`image${index + 1}` as keyof typeof values.images]}
                                                alt={`Preview ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <>
                                                <CiCamera className="text-2xl sm:text-5xl text-primary-color"/>
                                                <span className="text-xs text-gray-500 text-center mt-1">Fotoğraf<br/>Ekle</span>
                                            </>
                                        )}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <button
                            type="submit"
                            className="bg-primary-color text-white px-4 sm:px-6 py-2 rounded text-sm sm:text-base disabled:opacity-50 w-56"
                            disabled={!values.rating || values.review === ''}
                        >
                            Değerlendir
                        </button>
                    </Form>
                )}
            </Formik>
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover/>
        </>
    );
};

export default SellerRateForm;
